import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import SendIcon from '@mui/icons-material/Send';
import { TextField } from '@HomaHealth/ui-component-library';

const useStyles = makeStyles()({
  chatContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: '#fff',
  },

  header: {
    padding: '14px 16px',
    borderBottom: '1px solid #e0e0e0',
    fontWeight: 600,
    fontSize: '16px',
    background: '#ffffff',
    position: 'sticky',
    top: 0,
    zIndex: 2,
  },

  messagesContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    backgroundColor: '#f7f7f7',
  },

  messageRowLeft: {
    display: 'flex',
    justifyContent: 'flex-start',
  },

  messageRowRight: {
    display: 'flex',
    justifyContent: 'flex-end',
  },

  botMessage: {
    maxWidth: '75%',
    padding: '12px 16px',
    borderRadius: '18px 18px 18px 3px',
    background: '#f1f1f1',
    wordBreak: 'break-word',
  },

  userMessage: {
    maxWidth: '75%',
    padding: '12px 16px',
    borderRadius: '18px 18px 3px 18px',
    background: '#1976d2',
    color: '#fff',
    wordBreak: 'break-word',
  },

  inputContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px',
    borderTop: '1px solid #e0e0e0',
  },

  inputWrapper: {
    flex: 1,
  },

  sendBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '42px',
    height: '42px',
    borderRadius: '12px',
    cursor: 'pointer',
  },

  sendBtnActive: {
    background: '#1976d2',
  },

  sendBtnDisabled: {
    background: '#d3d3d3',
    cursor: 'not-allowed',
  },

  iconBtn: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  filePreview: {
    padding: '8px 12px',
    borderTop: '1px solid #eaeaea',
  },

  fileItem: {
    fontSize: '13px',
    marginBottom: '4px',
  },

  typingText: {
    fontSize: '13px',
    color: '#777',
  },

  uploadedLink: {
    display: 'block',
    color: '#1976d2',
    fontSize: '12px',
    marginTop: '4px',
    cursor: 'pointer',
    textDecoration: 'underline',
  },
});

export type FileResponse = {
  id: string;
  name: string;
  url: string;
};

export type ChatMessage = {
  id: number;
  text: string;
  sender: 'user' | 'bot';
  files?: File[];
  uploadedFiles?: FileResponse[];
};

export type SendPayload = {
  message: string;
  files: File[];
};

type SendResponse = {
  text: string;
  files?: FileResponse[];
};

type ChatModalProps = {
  title?: string;
  placeholder?: string;
  allowFileUpload?: boolean;
  acceptedFileTypes?: string[];
  showTypingIndicator?: boolean;

   
  onSend?: (payload: SendPayload) => Promise<SendResponse>;
};

const ChatModal = ({
  title = 'Chat Assistant',
  placeholder = 'Type a message...',
  allowFileUpload = true,
  acceptedFileTypes = ['application/pdf'],
  showTypingIndicator = true,
  onSend,
}: ChatModalProps) => {
  const { classes } = useStyles();

  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 1,
      text: 'Hello 👋 How can I help you?',
      sender: 'bot',
    },
  ]);

  const [input, setInput] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const handleSend = async () => {
    if (!input.trim() && !selectedFiles.length) {
      return;
    }

    const userMessage: ChatMessage = {
      id: Date.now(),
      text: input.trim(),
      sender: 'user',
      files: [...selectedFiles],
    };

    setMessages((prev) => [...prev, userMessage]);

    const payload: SendPayload = {
      message: input.trim(),
      files: [...selectedFiles],
    };

    setInput('');
    setSelectedFiles([]);

    if (!onSend) {
      return;
    }

    setLoading(true);

    try {
      const response = await onSend(payload);

      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          text: response.text,
          sender: 'bot',
          uploadedFiles: response.files,
        },
      ]);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);

    const validFiles = files.filter((file) =>
      acceptedFileTypes.includes(file.type),
    );

    setSelectedFiles((prev) => [...prev, ...validFiles]);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Box className={classes.chatContainer}>
      <Box className={classes.header}>{title}</Box>

      <Box className={classes.messagesContainer}>
        {messages.map((message) => (
          <Box
            key={message.id}
            className={
              message.sender === 'user'
                ? classes.messageRowRight
                : classes.messageRowLeft
            }
          >
            <Box
              className={
                message.sender === 'user'
                  ? classes.userMessage
                  : classes.botMessage
              }
            >
              <div>{message.text}</div>

              {message.files?.map((file) => (
                <div key={file.name} className={classes.fileItem}>
                  📄 {file.name}
                </div>
              ))}

              {message.uploadedFiles?.map((file) => (
                <a
                  key={file.id}
                  href={file.url}
                  target="_blank"
                  rel="noreferrer"
                  className={classes.uploadedLink}
                >
                  📎 {file.name}
                </a>
              ))}
            </Box>
          </Box>
        ))}

        {loading && showTypingIndicator && (
          <Box className={classes.messageRowLeft}>
            <Box className={classes.typingText}>Typing...</Box>
          </Box>
        )}

        <div ref={messagesEndRef} />
      </Box>

      {selectedFiles.length > 0 && (
        <Box className={classes.filePreview}>
          {selectedFiles.map((file) => (
            <div key={file.name} className={classes.fileItem}>
              📄 {file.name}
            </div>
          ))}
        </Box>
      )}

      <Box className={classes.inputContainer}>
        {allowFileUpload && (
          <Box
            className={classes.iconBtn}
            onClick={() => fileInputRef.current?.click()}
          >
            <AttachFileIcon />
          </Box>
        )}

        <Box className={classes.inputWrapper}>
          <TextField
            fullWidth
            size="small"
            placeholder={placeholder}
            variant="standard"
            value={input}
            onChange={(e: any) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSend();
              }
            }}
          />
        </Box>

        <Box
          onClick={handleSend}
          className={`${classes.sendBtn} ${
            input.trim() || selectedFiles.length
              ? classes.sendBtnActive
              : classes.sendBtnDisabled
          }`}
        >
          <SendIcon style={{ color: '#fff' }} />
        </Box>
      </Box>

      <input
        ref={fileInputRef}
        type="file"
        hidden
        multiple
        accept={acceptedFileTypes.join(',')}
        onChange={handleFileSelect}
      />
    </Box>
  );
};

export default ChatModal;
